//@ts-check
import { useContext, useState } from "react"
import { css } from "@emotion/react"
import Cta from "../_control/Cta"
import MultipleTestCategorySelectionGrid from "../testCategory/MultipleTestCategorySelectionGrid"
import DateTimeRangePicker from "./DateTimeRangePicker"
import TabBox from "../_layout/TabBox"
import SwitcherSimple from "../_control/SwitcherSimple"
import ProgressBar from "../_general/ProgressBar"
import TextField from "../_control/TextField"
import Aborter from "./Aborter"
import TabNavigator from "./TabNavigator"
import GeneralReportContext from "../../context/report-generator/GeneralReport"
import MultiplePitchSelection from "../pitch/MultiplePitchSelection"
import { colors } from "shared-ui"
import { lighten } from "polished"
import Loader from "../_general/Loader"
import useFormattedError from "../../hooks/useFormattedError"
import pdfDownloadIcon from '@assets/icons/pdf-download-icon.svg'
import { ReactSVG } from "react-svg"
import { useIntl } from "react-intl"
import FancyDropdown from "../_control/FancyDropdown"

export default function GeneralReportForm({
    reportTypeOptions,
    reportType,
    setReportType,
    ...props
}) {

    const [activeTabId, setActiveTabId] = useState(1)
    const { getErrorMessageFromCode } = useFormattedError()
    const { formatMessage } = useIntl()

    //period
    const {
        endDate, startDate, setEndDate, setStartDate,
        includeTests, includeCalendar, handleSetIncludeTests, handleSetIncludeCalendar,
        selectedPitches, setSelectedPitches,
        testCategories, selectedTestCategories, setSelectedTestCategories,
        allowFetches, abortFetches, handleFetchAllData, fetchingBusyState, dataReady, retryFetchData,
        firstStepValid, secondStepValid,
        reportTitle, setReportTitle, organisationName, setOrganisationName,
        includeCoverPage, setIncludeCoverPage,
        includeTestCatDescription, setIncludeTestCatDescription,
        includeTestsOverview, setIncludeTestsOverview,
        includeTestsBarCharts, setIncludeTestsBarCharts,
        includeCalendarTotals, setIncludeCalendarTotals,
        includeContactPage, setIncludeContactPage,
        includeLogo, setIncludeLogo,
        data,
        submitReportGenerationRequest,
        pdf,
        avoidColorConflicts, setAvoidColorConflicts,
        printFriendly, setPrintFriendly,
    } = useContext(GeneralReportContext)

    return <TabBox
        onClickTab={(tabId) => { setActiveTabId(tabId) }}
        showHeader={true}
        activeTabId={activeTabId}
        css={css`max-width: 80em;`}
        tabs={[
            {
                id: 1,
                name: formatMessage({ id: 'context' }),
                clickable: (activeTabId > 1),
                children: <div css={style.wrapper}>
                    <h2 css={style.stepTitle}>
                        {formatMessage({ id: 'reportType' })}
                    </h2>
                    <div css={css`
                        display: flex;
                        align-items: center;
                        gap: 0.5em 2em;
                    `}>
                        <FancyDropdown
                            options={reportTypeOptions}
                            value={reportTypeOptions.find(o => o.value === reportType.value)}
                            onChange={v => setReportType(v)}
                            css={css`max-width: 17em; flex-grow: 1;`}
                        />
                        <h4 css={css`
                            color: ${colors.light};
                        `}>
                            {reportType.tooltip}
                        </h4>

                    </div>

                    <hr css={style.divider} />
                    <DateTimeRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        showTimePicker={false}
                    />

                    <hr css={style.divider} />
                    <h2 css={style.stepTitle}>
                        {formatMessage({ id: 'sectionsToReport' })}
                    </h2>
                    <SwitcherSimple
                        checked={includeTests}
                        onClick={handleSetIncludeTests}
                        checkedLabel={formatMessage({ id: 'testData' })}
                        css={style.switcher}
                    />
                    <SwitcherSimple
                        checked={includeCalendar}
                        onClick={handleSetIncludeCalendar}
                        checkedLabel={formatMessage({ id: 'calendarData' })}
                        css={style.switcher}
                    />
                    <hr css={style.divider} />
                    <MultiplePitchSelection
                        selectedPitches={selectedPitches}
                        setSelectedPitches={setSelectedPitches}
                        maxPitches={30}
                    />
                    <TabNavigator
                        onNextClick={() => {
                            if (!includeTests) {
                                allowFetches()
                                handleFetchAllData()
                            }
                            setActiveTabId(includeTests ? 2 : 3)
                        }}
                        nextStepValid={firstStepValid}
                    />
                </div>,
            },
            {
                id: 2,
                name: formatMessage({ id: 'tests' }),
                clickable: includeTests && (activeTabId > 2),
                children: <div css={style.wrapper}>
                    <MultipleTestCategorySelectionGrid
                        testCategories={testCategories}
                        selectedCatIds={selectedTestCategories}
                        setSelectedCatIds={setSelectedTestCategories}
                        includeLab={false}
                    />
                    <TabNavigator
                        onPrevClick={() => setActiveTabId(1)}
                        nextStepValid={secondStepValid}
                        onNextClick={() => {
                            allowFetches()
                            handleFetchAllData()
                            setActiveTabId(3)
                        }}
                    />
                </div>,
            },
            {
                id: 3,
                name: formatMessage({ id: 'options' }),
                clickable: (activeTabId > 3),
                children: <div css={style.wrapper}>
                    <h2 css={style.stepTitle}>
                        {fetchingBusyState?.msg}
                        {dataReady &&
                            formatMessage({ id: 'dataReady' })
                        }
                    </h2>
                    <ProgressBar value={fetchingBusyState?.progress ?? 0} />
                    <Aborter abort={abortFetches} />

                    {!data &&
                        <Cta onClick={retryFetchData}>
                            {formatMessage({ id: 'retry' })}
                        </Cta>
                    }

                    <hr css={style.divider} />

                    <h2 css={style.stepTitle}>
                        {formatMessage({ id: 'reportTitle' })}
                    </h2>
                    <TextField
                        value={reportTitle}
                        onChange={(e) => setReportTitle(e.target.value)}
                        css={style.textField}
                    />

                    <h2 css={style.stepTitle}>
                        {formatMessage({ id: 'organisationName' })}
                    </h2>
                    <div css={style.organisationInput}>
                        <TextField
                            value={organisationName}
                            onChange={(e) => setOrganisationName(e.target.value)}
                            css={style.textField}
                        />
                        <img
                            css={style.logo(includeLogo)}
                            alt={data?.club?.name}
                            src={data?.club?.logo?.file}
                        />
                    </div>

                    <div css={style.optionTogglesGrid}>
                        <SwitcherSimple
                            checked={includeCoverPage}
                            onClick={() => setIncludeCoverPage(curr => !curr)}
                            checkedLabel={formatMessage({ id: 'coverPage' })}
                            css={style.switcher}
                        />
                        <SwitcherSimple
                            checked={includeLogo}
                            onClick={() => setIncludeLogo(curr => !curr)}
                            checkedLabel={formatMessage({ id: 'logo' })}
                            css={style.switcher}
                        />
                        {includeTests &&
                            <>
                                <SwitcherSimple
                                    checked={includeTestCatDescription}
                                    onClick={() => setIncludeTestCatDescription(curr => !curr)}
                                    checkedLabel={formatMessage({ id: 'testCategoryDescriptions' })}
                                    css={style.switcher}
                                />
                                <SwitcherSimple
                                    checked={includeTestsOverview}
                                    onClick={() => setIncludeTestsOverview(curr => !curr)}
                                    checkedLabel={formatMessage({ id: 'testsOverviewChart' })}
                                    css={style.switcher}
                                />
                                <SwitcherSimple
                                    checked={includeTestsBarCharts}
                                    onClick={() => setIncludeTestsBarCharts(curr => !curr)}
                                    checkedLabel={formatMessage({ id: 'testsBarChart' })}
                                    css={style.switcher}
                                />
                            </>
                        }
                        {includeCalendar &&
                            <SwitcherSimple
                                checked={includeCalendarTotals}
                                onClick={() => setIncludeCalendarTotals(curr => !curr)}
                                checkedLabel={formatMessage({ id: 'calendarTotalsChart' })}
                                css={style.switcher}
                            />
                        }
                        <SwitcherSimple
                            checked={includeContactPage}
                            onClick={() => setIncludeContactPage(curr => !curr)}
                            checkedLabel={formatMessage({ id: 'contactPage' })}
                            css={style.switcher}
                        />
                    </div>

                    <hr css={style.divider} />
                    <div css={style.optionTogglesGrid}>
                        <SwitcherSimple
                            checked={avoidColorConflicts}
                            onClick={() => setAvoidColorConflicts(curr => !curr)}
                            checkedLabel={formatMessage({ id: 'minimizeIdenticalPitchColors' })}
                            css={style.switcher}
                        />
                        <SwitcherSimple
                            checked={printFriendly}
                            onClick={() => setPrintFriendly(curr => !curr)}
                            checkedLabel={formatMessage({ id: 'printFriendlyMode' })}
                            css={style.switcher}
                        />
                    </div>

                    <TabNavigator
                        onPrevClick={() => setActiveTabId(includeTests ? 2 : 1)}
                        nextStepValid={dataReady}
                        onNextClick={() => {
                            setActiveTabId(4)
                            submitReportGenerationRequest()
                        }}
                    />
                </div>,
            },
            {
                id: 4,
                name: formatMessage({ id: 'download' }),
                clickable: activeTabId > 4,
                children: <div css={style.wrapper}>
                    <div css={css`
                        display: grid;
                        min-height: 10em;
                        place-items: center;
                    `}>
                        {pdf.busy &&
                            <h2 css={style.stepTitle}>
                                {formatMessage({ id: 'generatingAndDownloadingPDF' })}
                            </h2>
                        }
                        {pdf.busy ?
                            <Loader size="2em" />
                            :
                            pdf.error ?
                                <div css={css`color: ${colors.warn};`}>
                                    {getErrorMessageFromCode(pdf.error.status)}
                                </div>
                                :
                                <Cta onClick={pdf.download}>
                                    <ReactSVG
                                        src={pdfDownloadIcon}
                                        css={css`width: 50px; height: auto; color: white;`}
                                    />
                                </Cta>
                        }
                    </div>

                    <TabNavigator
                        onPrevClick={() => setActiveTabId(3)}
                    />
                </div>,
            },
        ].filter(tab => !includeTests ? tab.id !== 2 : true)
        }
    />
}

const style = {
    wrapper: css` 
        display: grid; 
        gap: 1em; 
    `,
    stepTitle: css`
        text-transform: uppercase;
    `,
    switcher: css`
        max-width: 30em;
    `,
    optionTogglesGrid: css`
        display: grid;
        grid-template-columns: 1fr 1fr;
        @media screen and (max-width: 600px) {
            grid-template-columns: 1fr;
        }
    `,
    organisationInput: css`
        display: grid;
        grid-template-columns: 1fr 6em;
    `,
    logo: (includeLogo) => css`
        opacity: ${includeLogo ? 1 : 0.1};
        width: 3.5em;
        height: auto;
        justify-self: end;
    `,
    textField: css`
        background: ${colors.eventLight}; 
        color: white;

        &:hover {
            background: ${lighten(0.05, colors.eventLight)};
        }
    `,
    divider: css`
        width: 100%;
        opacity: 0.2;
    `
}